import React, { createContext, useContext, useState, useEffect } from "react";
import { getPiRuntime, PiRuntimeState } from "../utils/env";

interface PiContextType {
    runtime: PiRuntimeState;
    isInitialized: boolean;
    error: string | null;
}

const PiContext = createContext<PiContextType | undefined>(undefined);

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

export const PiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [runtime, setRuntime] = useState<PiRuntimeState>(getPiRuntime());
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        const bootstrapSequence = async () => {
            const currentRuntime = getPiRuntime();

            // If we aren't in a Pi-Only build OR a Pi-detected environment, initialize immediately as normal web layout
            if (!currentRuntime.isPiOnlyBuild && !currentRuntime.isPiBrowser) {
                if (active) {
                    setRuntime(currentRuntime);
                    setIsInitialized(true);
                }
                return;
            }

            // Poll for the window wrapper injection engine
            let sdkMounter = false;
            for (let attempt = 0; attempt < 32; attempt++) {
                if ((window as any).Pi) {
                    sdkMounter = true;
                    break;
                }
                await wait(250);
            }

            if (!sdkMounter) {
                if (active) {
                    setError("Pi SDK script failed to load. Please make sure you are inside the official Pi Browser.");
                    setIsInitialized(true);
                }
                return;
            }

            try {
                const isSandbox = String(import.meta.env.VITE_PI_SANDBOX || "").toLowerCase() === "true";
                await (window as any).Pi.init({ version: "2.0", sandbox: isSandbox });

                if (active) {
                    setRuntime(getPiRuntime());
                }
            } catch (err: any) {
                console.warn("Pi SDK Init tracking notice:", err.message);
            } finally {
                if (active) setIsInitialized(true);
            }
        };

        bootstrapSequence();
        return () => { active = false; };
    }, []);

    return (
        <PiContext.Provider value={{ runtime, isInitialized, error }}>
            {children}
        </PiContext.Provider>
    );
};

export const usePi = () => {
    const context = useContext(PiContext);
    if (!context) throw new Error("usePi must be wrapped within a valid PiProvider wrapper container.");
    return context;
};